import { useState, useEffect } from "react";
import {
  Button,
  Paper,
  Typography,
  Grid,
  TextField,
  Box,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Document,
  Page,
  Text,
  View,
  pdf,
  Font
} from '@react-pdf/renderer';
import { ServiceContractSample } from '../../Components/Templates/ServiceContractSample';
import { pdfStyles } from '../../Styles/contractPdfStyles';
import { Vocabulary } from '../../Utils/Vocabulary';
import styles from "../../Styles/loginPage.module.css";
import contractStyles from "../../Styles/serviceDor2Dor.module.css";
import { ContractData } from "../../Components/ProductSettings/Types/Types";
import { postData } from "../../Services/postData";
import { getData } from "../../Services/getData";
import { updateData } from "../../Services/updateData";
import { urlEnum } from "../../Utils/UrlEnum";
import { toast } from "react-toastify";
import { ServiceDocumentValidationSchema, ServiceDocumentFormData } from "../../Utils/ValidationSchemas";

// Register Romanian-compatible fonts
Font.register({
  family: 'Roboto',
  fonts: [
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf',
      fontWeight: 300
    },
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf',
      fontWeight: 400
    },
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-medium-webfont.ttf',
      fontWeight: 500
    },
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf',
      fontWeight: 700
    }
  ]
});



const ContractPDF = ({ data }: { data: ContractData; }) => (
  <Document>
    <Page size="A4" style={pdfStyles.page}>
      <Text style={pdfStyles.title}>{Vocabulary.contractTitle}</Text>
      <Text style={pdfStyles.subtitle}>{Vocabulary.contractSubtitle}</Text>

      <View style={pdfStyles.section}>
        <Text style={pdfStyles.text}>
          {Vocabulary.contractDateText}, {data.contractDate}, {Vocabulary.contractBetween}:
        </Text>
      </View>

      <View style={pdfStyles.section}>
        <Text style={pdfStyles.articleTitle}>{Vocabulary.providerLabel}</Text>
        <Text style={pdfStyles.text}>
          {data.providerCompany || Vocabulary.providerCompanyPlaceholder}, {Vocabulary.prestatorAddressPrefix} {data.providerAddress || '__________'},
          {Vocabulary.prestatorCUIPrefix} {data.providerCUI || '__________'}, {Vocabulary.prestatorJPrefix} {data.providerJ || '__________'},
          {Vocabulary.prestatorRepresentativePrefix} {data.providerRepresentative || '__________'},
          {Vocabulary.prestatorSuffix}
        </Text>
      </View>

      <View style={pdfStyles.section}>
        <Text style={pdfStyles.text}>{Vocabulary.clientAnd}</Text>
      </View>

      <View style={pdfStyles.section}>
        <Text style={pdfStyles.articleTitle}>{Vocabulary.clientLabel}</Text>
        <Text style={pdfStyles.text}>
          {data.clientName || Vocabulary.clientNamePlaceholder} {Vocabulary.clientAddressPrefix} {data.clientAddress || '__________'},
          {Vocabulary.clientCNPPrefix} {data.clientCNP || '__________'}, {Vocabulary.clientSuffix}
        </Text>
      </View>

      <View style={pdfStyles.section}>
        <Text style={pdfStyles.articleTitle}>{Vocabulary.contractArticle1Title}</Text>
        <Text style={pdfStyles.text}>
          {Vocabulary.contractArticle1Text}
        </Text>
      </View>

      <View style={pdfStyles.section}>
        <Text style={pdfStyles.articleTitle}>{Vocabulary.contractArticle2Title}</Text>
        <Text style={pdfStyles.text}>
          {Vocabulary.contractArticle2Text}
        </Text>
      </View>

      <View style={pdfStyles.section}>
        <Text style={pdfStyles.articleTitle}>{Vocabulary.contractArticle3Title}</Text>
        <Text style={pdfStyles.text}>
          {Vocabulary.contractArticle3Text}
        </Text>
      </View>

      <View style={pdfStyles.section}>
        <Text style={pdfStyles.articleTitle}>{Vocabulary.contractArticle4Title}</Text>
        <Text style={pdfStyles.text}>
          {Vocabulary.contractArticle4Text}
        </Text>
      </View>

      <View style={pdfStyles.section}>
        <Text style={pdfStyles.articleTitle}>{Vocabulary.contractArticle5Title}</Text>
        <Text style={pdfStyles.text}>
          {Vocabulary.contractArticle5Text}
        </Text>
      </View>

      <View style={pdfStyles.section}>
        <Text style={pdfStyles.articleTitle}>{Vocabulary.contractArticle6Title}</Text>
        <Text style={pdfStyles.text}>
          {Vocabulary.contractArticle6Text}
        </Text>
      </View>

      <View style={pdfStyles.section}>
        <Text style={pdfStyles.articleTitle}>{Vocabulary.contractArticle7Title}</Text>
        <Text style={pdfStyles.text}>
          {Vocabulary.contractArticle7Text}
        </Text>
      </View>

      <View style={pdfStyles.section}>
        <Text style={pdfStyles.articleTitle}>{Vocabulary.contractArticle8Title}</Text>
        <Text style={pdfStyles.text}>
          {Vocabulary.contractArticle8Text}
        </Text>
      </View>

      <View style={pdfStyles.signatureSection}>
        <View style={pdfStyles.signatureBox}>
          <Text style={pdfStyles.text}>{Vocabulary.contractPrestatorSignature}</Text>
          <View style={pdfStyles.signatureLine} />
        </View>
        <View style={pdfStyles.signatureBox}>
          <Text style={pdfStyles.text}>{Vocabulary.contractClientSignature}</Text>
          <View style={pdfStyles.signatureLine} />
        </View>
      </View>
    </Page>
  </Document>
);

export default function ServiceDoorToDoor() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id: contractId } = useParams<{ id: string; }>();
  const [loading, setLoading] = useState(false);
  const [isPDFGenerated, setIsPDFGenerated] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [clientId, setClientId] = useState<string | null>(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [originalContractData, setOriginalContractData] = useState<any>(null);

  // Initialize react-hook-form with yup validation
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    watch,
  } = useForm<ServiceDocumentFormData>({
    resolver: yupResolver(ServiceDocumentValidationSchema),
    defaultValues: {
      contractDate: new Date().toLocaleDateString('ro-RO'),
      providerCompany: 'S.C. LOGPINSAV S.R.L',
      providerAddress: 'Str. Nicolae Iorga nr.14A, bl.14, Sc.A, Ap.5, mun. Suceava, jud. Suceava',
      providerCUI: 'RO30208853',
      providerJ: 'J33/379/2012',
      providerRepresentative: 'S.C. LOGPINSAV S.R.L',
      clientName: '',
      clientAddress: '',
      clientCNP: '',
    },
    mode: 'onChange', // Validate on change
  });

  // Watch all form values for PDF generation
  const contractData = watch();
  // Load existing contract data if contractId is provided
  useEffect(() => {
    const loadContractData = async () => {
      if (contractId) {
        try {
          setLoading(true);
          setIsViewMode(true);
          const response = await getData(`${urlEnum.client.serviceContracts}/${contractId}`);
          if (response?.data) {
            const contract = response.data;
            setOriginalContractData(contract);
            const dataToLoad = contract.contractData || {
              contractDate: new Date().toLocaleDateString('ro-RO'),
              providerCompany: 'S.C. LOGPINSAV S.R.L',
              providerAddress: 'Str. Nicolae Iorga nr.14A, bl.14, Sc.A, Ap.5, mun. Suceava, jud. Suceava',
              providerCUI: 'RO30208853',
              providerJ: 'J33/379/2012',
              providerRepresentative: 'S.C. LOGPINSAV S.R.L',
              clientName: '',
              clientAddress: '',
              clientCNP: '',
            };
            reset(dataToLoad);
            setClientId(contract.clientId);
            setIsPDFGenerated(true); // Contract already exists
          }
        } catch (error) {
          console.error('Error loading contract:', error);
          toast.error(Vocabulary.errorLoadingContract || 'Error loading contract');
        } finally {
          setLoading(false);
        }
      } else {
        // Handle new contract creation (from clientId query param)
        const clientId = searchParams.get('clientId');
        if (clientId) {
          setClientId(clientId);
        }
        setLoading(false);
      }
    };

    loadContractData();
  }, [contractId, searchParams, reset]);

  // Form submission handler
  const onSubmit = async (formData: ServiceDocumentFormData) => {
    await saveDocument(formData);
  };

  const generateAndDownloadPDF = async () => {
    try {
      setIsGeneratingPDF(true);

      // Generate PDF blob
      const pdfDoc = <ContractPDF data={contractData} />;
      const pdfBlob = await pdf(pdfDoc).toBlob();

      // Create download link
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contract-service-${contractData.contractDate.replace(/\//g, '-')}.pdf`;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setIsPDFGenerated(true);
      toast.success(Vocabulary.pdfGeneratedSuccessfully);

    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(Vocabulary.errorGeneratingPDF);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const saveDocument = async (formData: ServiceDocumentFormData) => {
    if (clientId === null) {
      toast.error(Vocabulary.missingClient);
      return;
    }

    try {
      setIsSaving(true);
      // Generate PDF blob
      const pdfDoc = <ContractPDF data={formData as ContractData} />;
      const pdfBlob = await pdf(pdfDoc).toBlob();

      // Convert blob to base64
      const base64PDF = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result as string;
          // Remove data:application/pdf;base64, prefix
          const base64Data = base64.split(',')[1];
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(pdfBlob);
      });

      const payload = {
        clientId,
        contractData: formData,
        pdfBuffer: base64PDF,
        fileName: `contract-service-${formData.contractDate.replace(/\//g, '-')}.pdf`
      };

      let response;
      if (isViewMode && contractId) {
        // Update existing contract
        response = await updateData(`${urlEnum.client.serviceContracts}/${contractId}`, payload);
      } else {
        // Create new contract
        response = await postData(urlEnum.client.serviceContract, payload);
      }

      if (response.status === 200 || response.status === 201) {
        toast.success(Vocabulary.savedSuccessfully);
        // Reset the form dirty state
        reset(formData);
        // Optionally navigate back or reset form
      }

    } catch (error) {
      console.error(Vocabulary.errorSavingContract, error);
      toast.error(Vocabulary.errorSavingContract);
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div >
      <div className={styles.blurOverlay} />
      <Paper className={`${styles.loginContainer} ${contractStyles.contractContainer}`} elevation={5}>
        <Box p={4} className={contractStyles.contractContent}>
          <Typography variant="h4" align="center" gutterBottom className={contractStyles.contractTitle}>
            {isViewMode ? `${Vocabulary.contractTitle} - ${Vocabulary.view || 'View'}` : Vocabulary.contractTitle}
          </Typography>
          <Typography variant="h6" align="center" gutterBottom className={contractStyles.contractSubtitle}>
            {Vocabulary.contractSubtitle}
          </Typography>
          {isViewMode && originalContractData && (
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">
                <strong>{Vocabulary.clientName || 'Client'}:</strong> {originalContractData.contractData?.clientName || 'N/A'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>{Vocabulary.createdAt || 'Created'}:</strong> {new Date(originalContractData.createdAt).toLocaleDateString('ro-RO')}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>{Vocabulary.fileName || 'File'}:</strong> {originalContractData.originalFileName || 'N/A'}
              </Typography>
            </Box>
          )}

          <Box component="form" onSubmit={handleSubmit(onSubmit)} className={contractStyles.contractForm}>
            <Typography variant="body1" paragraph>
              {Vocabulary.contractDateText},
              <Controller
                name="contractDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="standard"
                    className={contractStyles.inlineDateField}
                    error={!!errors.contractDate}
                    helperText={errors.contractDate?.message}
                  />
                )}
              />
              , {Vocabulary.contractBetween}:
            </Typography>

            <Typography variant="h6" gutterBottom className={contractStyles.sectionTitle}>
              {Vocabulary.providerLabel}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="providerCompany"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      variant="outlined"
                      label={Vocabulary.contractCompanyLabel}
                      disabled={true}
                      size="small"
                      error={!!errors.providerCompany}
                      helperText={errors.providerCompany?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="providerAddress"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      variant="outlined"
                      label={Vocabulary.contractAddressLabel}
                      disabled={true}
                      size="small"
                      error={!!errors.providerAddress}
                      helperText={errors.providerAddress?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  name="providerCUI"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      variant="outlined"
                      label={Vocabulary.contractCUILabel}
                      disabled={true}
                      size="small"
                      error={!!errors.providerCUI}
                      helperText={errors.providerCUI?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  name="providerJ"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      variant="outlined"
                      label={Vocabulary.contractJLabel}
                      disabled={true}
                      size="small"
                      error={!!errors.providerJ}
                      helperText={errors.providerJ?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="providerRepresentative"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      variant="outlined"
                      label={Vocabulary.contractRepresentativeLabel}
                      disabled={true}
                      size="small"
                      error={!!errors.providerRepresentative}
                      helperText={errors.providerRepresentative?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Typography variant="body1" paragraph className={contractStyles.contractText}>
              {Vocabulary.prestatorSuffix}
            </Typography>

            <Typography variant="body1" paragraph>
              {Vocabulary.clientAnd}
            </Typography>

            <Typography variant="h6" gutterBottom>
              {Vocabulary.clientLabel}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <Controller
                  name="clientName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      variant="outlined"
                      label={Vocabulary.contractClientNameLabel}
                      size="small"
                      error={!!errors.clientName}
                      helperText={errors.clientName?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="clientCNP"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      variant="outlined"
                      label={Vocabulary.contractClientCNPLabel}
                      size="small"
                      error={!!errors.clientCNP}
                      helperText={errors.clientCNP?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="clientAddress"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      variant="outlined"
                      label={Vocabulary.contractClientAddressLabel}
                      size="small"
                      error={!!errors.clientAddress}
                      helperText={errors.clientAddress?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Typography variant="body1" paragraph className={contractStyles.contractText}>
              {Vocabulary.clientSuffix}
            </Typography>

            <ServiceContractSample
              contractData={contractData}
              handleFieldChange={() => undefined}
            />

            <Box className={contractStyles.signatureSection}>
              <Typography variant="body1">
                <strong>{Vocabulary.provider}</strong><br />
                ___________________
              </Typography>
              <Typography variant="body1">
                <strong>{Vocabulary.client}</strong><br />
                ___________________
              </Typography>
            </Box>

            <Box className={contractStyles.buttonContainer}>
              <Button
                variant="outlined"
                onClick={handleBack}
                size="large"
                className={contractStyles.backButton}
              >
                {Vocabulary.contractBackButton}
              </Button>

              {/* Download PDF button (always available) */}
              <Button
                variant="outlined"
                size="large"
                className={contractStyles.pdfButton}
                disabled={loading || isGeneratingPDF}
                onClick={generateAndDownloadPDF}
                style={{ marginLeft: 8 }}
              >
                {isGeneratingPDF ? Vocabulary.loading : (Vocabulary.contractGeneratePDFButton || 'Download PDF')}
              </Button>

              {/* Save/Update button */}
              {isViewMode ? (
                isDirty && (
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    className={contractStyles.pdfButton}
                    disabled={loading || isSaving || Object.keys(errors).length > 0}
                    style={{ marginLeft: 8 }}
                  >
                    {isSaving ? Vocabulary.saving : (Vocabulary.updateContract || 'Update Contract')}
                  </Button>
                )
              ) : (
                isPDFGenerated && (
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    className={contractStyles.pdfButton}
                    disabled={loading || isSaving || Object.keys(errors).length > 0}
                    style={{ marginLeft: 8 }}
                  >
                    {isSaving ? Vocabulary.saving : Vocabulary.contractSave}
                  </Button>
                )
              )}
            </Box>
          </Box>
        </Box>
      </Paper>
    </div>
  );
}
